// AdminPage.jsx - Refactored Version
// All managers are now in separate files under /components/admin/

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  Package, 
  Users, 
  Handshake, 
  Calendar, 
  Image as ImageIcon,
  FileText, 
  MessageCircle, 
  Settings, 
  LogOut,
  Loader2,
  Award,
  SlidersHorizontal,
  Mail,
  MessageSquare,
  BookImage,
  Database,
  Server
} from 'lucide-react';
import { Button } from '../components/ui/button';

// Import all admin managers
import {
  PageImagesManager,
  ContentManager,
  PartnersManager,
  ReferencesManager,
  EventsManager,
  EquipmentManager,
  HeroSlidesManager,
  FooterManager,
  ContactInfoManager,
  MessagesManager,
  ChatManager,
  UsersManager,
  FeedbacksManager
} from '../components/admin';
import BackupManager from '../components/admin/BackupManager';
import SystemManager from '../components/admin/SystemManager';
import LogoManager from '../components/admin/LogoManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ============== MAIN ADMIN PAGE ==============
const AdminPage = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if user is authenticated and admin
    const checkAuth = async () => {
      if (!isAuthenticated) {
        navigate('/login');
        return;
      }
      setIsLoading(false);
    };
    checkAuth();
  }, [isAuthenticated, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const menuItems = [
    { id: 'dashboard', label: 'Tableau de Bord', icon: LayoutDashboard },
    // --- Apparence ---
    { id: 'logo', label: 'Logos & Favicon', icon: ImageIcon },
    { id: 'hero', label: 'Slider Accueil', icon: SlidersHorizontal },
    { id: 'page-images', label: 'Images des Pages', icon: BookImage },
    // --- Contenu ---
    { id: 'content', label: 'Textes du Site', icon: FileText },
    { id: 'equipment', label: 'Catalogue Équipements', icon: Package },
    { id: 'partners', label: 'Partenaires', icon: Handshake },
    { id: 'references', label: 'Références Clients', icon: Award },
    { id: 'events', label: 'Événements', icon: Calendar },
    // --- Contact & Communication ---
    { id: 'contact-info', label: 'Équipe / Contacts', icon: Users },
    { id: 'footer', label: 'Pied de Page', icon: Settings },
    { id: 'messages', label: 'Messages Reçus', icon: Mail },
    { id: 'feedbacks', label: 'Demandes Partenariat', icon: MessageSquare },
    { id: 'chat', label: 'Chat en Direct', icon: MessageCircle },
    // --- Administration ---
    { id: 'users', label: 'Utilisateurs Admin', icon: Users },
    { id: 'backup', label: 'Sauvegarde BDD', icon: Database },
    { id: 'system', label: 'Paramètres Système', icon: Server },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <Dashboard setActiveTab={setActiveTab} />;
      case 'logo':
        return <LogoManager />;
      case 'hero':
        return <HeroSlidesManager />;
      case 'content':
        return <ContentManager />;
      case 'page-images':
        return <PageImagesManager />;
      case 'equipment':
        return <EquipmentManager />;
      case 'partners':
        return <PartnersManager />;
      case 'references':
        return <ReferencesManager />;
      case 'events':
        return <EventsManager />;
      case 'contact-info':
        return <ContactInfoManager />;
      case 'footer':
        return <FooterManager />;
      case 'messages':
        return <MessagesManager />;
      case 'feedbacks':
        return <FeedbacksManager />;
      case 'chat':
        return <ChatManager />;
      case 'users':
        return <UsersManager />;
      case 'backup':
        return <BackupManager />;
      case 'system':
        return <SystemManager />;
      default:
        return <Dashboard setActiveTab={setActiveTab} />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-slate-200 flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-slate-100">
          <h1 className="text-xl font-bold text-slate-800">SAE Admin</h1>
          <p className="text-xs text-slate-400 mt-1">Panneau d'administration</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-colors ${
                  activeTab === item.id
                    ? 'bg-slate-100 text-slate-900 font-medium'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                <Icon className="w-4 h-4" />
                {item.label}
              </button>
            );
          })}
        </nav>

        {/* User info */}
        <div className="p-4 border-t border-slate-100">
          <div className="flex items-center gap-3 mb-3">
            {user?.picture && (
              <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-700 truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <Button variant="outline" className="w-full" size="sm" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" /> Déconnexion
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-y-auto">
        {renderContent()}
      </main>
    </div>
  );
};

// ============== DASHBOARD ==============
const Dashboard = ({ setActiveTab }) => {
  const [stats, setStats] = useState({
    partners: 0,
    equipment: 0,
    references: 0,
    events: 0,
    messages: 0,
    feedbacks: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [partnersRes, equipmentRes, referencesRes, eventsRes, messagesRes, feedbacksRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/partners`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/equipment-categories`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/references`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/events`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/admin/contacts`, { credentials: 'include' }).catch(() => ({ ok: false })),
        fetch(`${BACKEND_URL}/api/admin/feedbacks`, { credentials: 'include' }).catch(() => ({ ok: false }))
      ]);

      const partners = partnersRes.ok ? await partnersRes.json() : [];
      const equipment = equipmentRes.ok ? await equipmentRes.json() : [];
      const references = referencesRes.ok ? await referencesRes.json() : [];
      const events = eventsRes.ok ? await eventsRes.json() : [];
      const messages = messagesRes.ok ? await messagesRes.json() : [];
      const feedbacks = feedbacksRes.ok ? await feedbacksRes.json() : [];

      setStats({
        partners: partners.length || 0,
        equipment: equipment.length || 0,
        references: references.length || 0,
        events: events.length || 0,
        messages: messages.length || 0,
        feedbacks: feedbacks.length || 0
      });
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const statCards = [
    { label: 'Partenaires', value: stats.partners, icon: Handshake, color: 'bg-blue-500', tab: 'partners' },
    { label: 'Équipements', value: stats.equipment, icon: Package, color: 'bg-green-500', tab: 'equipment' },
    { label: 'Références', value: stats.references, icon: Award, color: 'bg-purple-500', tab: 'references' },
    { label: 'Événements', value: stats.events, icon: Calendar, color: 'bg-orange-500', tab: 'events' },
    { label: 'Messages', value: stats.messages, icon: Mail, color: 'bg-red-500', tab: 'messages' },
    { label: 'Demandes', value: stats.feedbacks, icon: MessageSquare, color: 'bg-teal-500', tab: 'feedbacks' },
  ];

  return (
    <div>
      <h1 className="text-3xl font-light text-slate-800 mb-8">Tableau de Bord</h1>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <button
              key={stat.label}
              onClick={() => setActiveTab(stat.tab)}
              className="bg-white rounded-xl p-4 border border-slate-200 hover:shadow-md transition-shadow text-left"
            >
              <div className={`w-10 h-10 ${stat.color} rounded-lg flex items-center justify-center mb-3`}>
                <Icon className="w-5 h-5 text-white" />
              </div>
              <p className="text-2xl font-semibold text-slate-800">{stat.value}</p>
              <p className="text-sm text-slate-500">{stat.label}</p>
            </button>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-lg font-medium text-slate-800 mb-4">Actions Rapides</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => setActiveTab('hero')}>
            <SlidersHorizontal className="w-5 h-5" />
            <span>Gérer Slider</span>
          </Button>
          <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => setActiveTab('equipment')}>
            <Package className="w-5 h-5" />
            <span>Ajouter Équipement</span>
          </Button>
          <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => setActiveTab('partners')}>
            <Handshake className="w-5 h-5" />
            <span>Ajouter Partenaire</span>
          </Button>
          <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => setActiveTab('events')}>
            <Calendar className="w-5 h-5" />
            <span>Ajouter Événement</span>
          </Button>
        </div>
      </div>

      {/* Info box */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h3 className="font-medium text-blue-900 mb-2">💡 Astuce</h3>
        <p className="text-sm text-blue-800">
          Utilisez la barre latérale pour naviguer entre les différentes sections. 
          Chaque section vous permet de gérer un aspect spécifique du site (contenu, images, partenaires, etc.)
        </p>
      </div>
    </div>
  );
};

export default AdminPage;
